import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { createContext, provide } from '@lit/context'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { TierPricingSingle } from 'components/discovery/pdp/tier-pricing/typing/tier-pricing-typing.js'

export type PdpQuantityContextType = number
export type PdpSelectedTierContextType = TierPricingSingle | null
export type PdpActionsContextType = {
  setQuantity: (quantity: number) => void
  setTier: (tier: TierPricingSingle | null) => void
}

export const PdpQuantityContext = createContext<PdpQuantityContextType>(Symbol('csc-pdp-quantity-context'))
export const PdpSelectedTierContext = createContext<PdpSelectedTierContextType>(Symbol('csc-pdp-selected-tier-context'))
export const PdpActionsContext = createContext<PdpActionsContextType>(Symbol('csc-pdp-actions-context'))

@customElement('csc-pdp-provider')
export class CSCPdpProvider extends LitElement {
  @property({ type: Number, attribute: 'product-id' })
  productId: number | null = null

  @inject(PDP_PRODUCT_STORE)
  private productStore!: PdpProductStore

  private productLoaded: Promise<void> = Promise.resolve()

  @provide({ context: PdpQuantityContext })
  quantity: PdpQuantityContextType = 0

  @provide({ context: PdpSelectedTierContext })
  selectedTier: PdpSelectedTierContextType = null

  @provide({ context: PdpActionsContext })
  actions!: PdpActionsContextType

  override connectedCallback(): void {
    super.connectedCallback()

    if (!this.actions) {
      this.actions = {
        setQuantity: (quantity) => {
          this.quantity = quantity
        },
        setTier: (tier) => {
          this.selectedTier = tier
        },
      }
    }

    this.productLoaded = this.productStore.loadProduct({ productId: this.productId })
  }

  override async getUpdateComplete(): Promise<boolean> {
    const result = await super.getUpdateComplete()
    await this.productLoaded
    return result
  }

  protected override createRenderRoot(): this {
    return this
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-pdp-provider': CSCPdpProvider
  }
}
